import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { View, Platform, TextInput, Pressable } from 'react-native';
import { Textarea, TextareaInput } from '@/components/gluestack/textarea';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { ImageUploadButton } from '@/components/ui/image/ImageUploadButton';
import { ContextSheet } from '@/components/ui/ContextSheet';
import { GifPickerSheet } from '@/components/ui/media/GifPickerSheet';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { SentIcon, Gif01Icon } from '@hugeicons-pro/core-stroke-rounded';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { useIsMobileWeb } from '@/hooks/useIsMobileWeb';
import { useAutoFocusEffect } from '@/hooks/useDelayedAutoFocus';
import { mediaRegistry } from '@/services/media/registry';
import type { IMediaItem, IMediaShareContext } from '@/models/Media';
import { useTranslation } from 'react-i18next';

const MIN_INPUT_HEIGHT = 40;
const MAX_INPUT_HEIGHT = 120;

/**
 * Reference interface for BottomTextInput component
 */
export interface IBottomTextInputRef {
  /**
   * Focus the text input field
   */
  focus: () => void;
}

/**
 * Props for the BottomTextInput component
 */
interface IBottomTextInputProps {
  /**
   * Callback function triggered when text is submitted
   * @param text The text that was submitted
   */
  onSubmit: (text: string) => Promise<void>;
  
  /**
   * Whether the input is currently submitting
   */
  isSubmitting: boolean;
  
  /**
   * Initial text value for the input field
   */
  initialText?: string;
  
  /**
   * Placeholder text to display when the input is empty
   */
  placeholder: string;
  
  /**
   * Color for the placeholder text
   */
  placeholderTextColor?: string;

  /**
   * Image upload mode:
   * - 'none': No image upload functionality (default)
   * - 'preview': Shows preview sheet before upload
   * - 'direct': Passes image URI directly to caller for immediate upload
   * @default 'none'
   */
  imageUploadMode?: 'none' | 'preview' | 'direct';

  /**
   * Callback to handle image upload (receives image URI or File)
   * @param imageUri The image URI or File object
   */
  onImageUpload?: (imageUri: string | File) => void;

  /**
   * Whether an image is currently being uploaded
   */
  isImageUploading?: boolean;

  /**
   * Optional callback invoked whenever the input text changes
   */
  onChangeText?: (text: string) => void;

  /**
   * When using imageUploadMode='preview', whether to show the caption field.
   * Defaults to true. For conversations, set to false.
   */
  showImageCaptionInPreview?: boolean;

  /**
   * Whether to auto-focus the input with a platform-appropriate delay.
   * @default false
   */
  autoFocus?: boolean;

  /**
   * On native platforms, dismiss the on-screen keyboard when the user taps a
   * menu item to pick an image. Useful in messaging-style surfaces so the
   * keyboard does not remain visible after the user returns from the picker.
   * @default false
   */
  dismissKeyboardOnImagePick?: boolean;

  /**
   * After a successful send, keep the text field focused so the keyboard stays
   * open (chat-style). When false, the input may be disabled while submitting
   * and focus is not restored after send.
   * @default false
   */
  keepKeyboardOpenAfterSubmit?: boolean;

  /**
   * GIF picker mode:
   * - 'none': No GIF picker (default)
   * - 'enabled': Shows an inline GIF button when the input is empty and a provider is configured
   */
  gifPickerMode?: 'none' | 'enabled';

  /**
   * Callback when the user selects a GIF/media item from the picker.
   */
  onMediaSelected?: (item: IMediaItem, ctx?: IMediaShareContext) => void | Promise<void>;
}



/**
 * A reusable text input component that appears at the bottom of the screen.
 * 
 * Features:
 * - Auto-growing text input that expands as content grows
 * - Platform-specific implementation (native auto-grow on iOS/Android, measured height on web)
 * - Send button that activates only when there's content
 * - Focus control via ref
 */
export const BottomTextInput = forwardRef<IBottomTextInputRef, IBottomTextInputProps>(
  ({ 
    onSubmit, 
    isSubmitting, 
    initialText = '', 
    placeholder, 
    placeholderTextColor = '#888888', 
    imageUploadMode = 'none',
    onImageUpload,
    isImageUploading = false,
    onChangeText,
    showImageCaptionInPreview = true,
    autoFocus = false,
    dismissKeyboardOnImagePick = false,
    keepKeyboardOpenAfterSubmit = false,
    gifPickerMode = 'none',
    onMediaSelected,
  }, ref) => {
    const { t } = useTranslation();
    const isLargeScreen = useIsLargeScreen();
    const isMobileWeb = useIsMobileWeb();
    const [inputText, setInputText] = useState(initialText);
    const [inputHeight, setInputHeight] = useState(MIN_INPUT_HEIGHT);
    const [isFocused, setIsFocused] = useState(false);
    const [isGifPickerOpen, setIsGifPickerOpen] = useState(false);

    const nativeInputRef = useRef<TextInput>(null);
    const webInputRef = useRef<any>(null);
    const submitInFlightRef = useRef(false);

    const isGifPickerAvailable =
      gifPickerMode === 'enabled' &&
      !!onMediaSelected &&
      !!mediaRegistry.defaultFor('gif')?.isConfigured();
    const showInlineGifButton = isGifPickerAvailable && inputText.length === 0;

    const inputWrapperClassName = `min-w-0 flex-1 overflow-hidden rounded-xl border bg-background-0 ${
      isFocused ? 'border-primary-600' : 'border-background-300'
    }`;

    const inlineGifButton = showInlineGifButton ? (
      <Pressable
        accessibilityLabel={t('media.gif_button_label')}
        accessibilityRole="button"
        disabled={isSubmitting || isImageUploading}
        onPress={() => setIsGifPickerOpen(true)}
        className="shrink-0 px-2"
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        {...(Platform.OS === 'web'
          ? { onMouseDown: (event: { preventDefault: () => void }) => event.preventDefault() }
          : {})}>
        <HugeIconsWrapper icon={Gif01Icon} size={24} className="color-typography-500" />
      </Pressable>
    ) : null;

    const renderTextInputField = () => {
      if (Platform.OS !== 'web') {
        // Native (iOS + Android): let the multiline input auto-grow natively up to
        // a max height. Driving the height from JS (onContentSizeChange -> setState
        // -> explicit height) forces a native re-layout of the EditText mid-keystroke,
        // which on the new architecture can detach the input (view flattening),
        // dismissing the keyboard and corrupting the IME composing text.
        // collapsable={false} keeps the wrappers from being flattened/unflattened
        // when their styles change (e.g. focus border), which also blurs the input.
        return (
          <View className={inputWrapperClassName} collapsable={false}>
            <View className="flex-row items-center" collapsable={false}>
              <TextInput
                ref={nativeInputRef}
                className={
                  Platform.OS === 'ios'
                    ? 'max-h-28 min-w-0 flex-1 px-4 py-3 text-base color-typography-900'
                    : 'max-h-[120px] min-h-10 min-w-0 flex-1 px-4 py-2 text-base color-typography-900'
                }
                multiline
                onChangeText={(text) => {
                  setInputText(text);
                  onChangeText?.(text);
                }}
                value={inputText}
                placeholder={placeholder}
                placeholderTextColor={placeholderTextColor}
                editable={isInputEditable}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                submitBehavior="newline"
              />
              {inlineGifButton}
            </View>
          </View>
        );
      }

      return (
        <View className={inputWrapperClassName}>
          <View className="flex-row items-center">
            <Textarea
              variant="flat"
              className="min-h-0 min-w-0 flex-1 border-0 bg-transparent"
              isDisabled={false}
              style={{ height: inputHeight }}>
              <TextareaInput
                ref={webInputRef}
                className="h-full px-4 py-2"
                value={inputText}
                onChangeText={(text) => {
                  setInputText(text);
                  onChangeText?.(text);
                }}
                placeholder={placeholder}
                placeholderTextColor={placeholderTextColor}
                editable={isInputEditable}
                submitBehavior="newline"
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                returnKeyType="default"
                onSubmitEditing={isMobileWeb ? handleSubmit : undefined}
                onKeyPress={(e: any) => {
                  if (e.key === 'Enter') {
                    if (isMobileWeb) {
                      return;
                    }

                    if (!e.shiftKey) {
                      e.preventDefault();
                      void handleSubmit();
                    }
                  }
                }}
              />
            </Textarea>
            {inlineGifButton}
          </View>
        </View>
      );
    };

    const focusInput = () => {
      if (Platform.OS === 'web') {
        webInputRef.current?.focus();
      } else {
        nativeInputRef.current?.focus();
      }
      setIsFocused(true);
    };

    // Expose focus method via ref
    useImperativeHandle(ref, () => ({ focus: focusInput }));

    useAutoFocusEffect(focusInput, autoFocus);

    const scheduleRefocusInput = () => {
      if (Platform.OS === 'web') {
        webInputRef.current?.focus?.();
        setTimeout(() => {
          webInputRef.current?.focus?.();
        }, 0);
        setIsFocused(true);
        return;
      }

      focusInput();
      requestAnimationFrame(() => {
        focusInput();
      });
    };

    const isInputEditable =
      !isImageUploading && (!isSubmitting || keepKeyboardOpenAfterSubmit);

    // Web: keep the input height in sync with its content. The textarea is
    // measured with its height collapsed so the result is the intrinsic content
    // height — reading scrollHeight at the current height (what RNW's
    // onContentSizeChange does) can never shrink and, with fractional browser
    // zoom, grows 1px per measurement in a feedback loop until React throws
    // "Maximum update depth exceeded".
    // Layout effect: from a passive effect, every keystroke queued a separate
    // background render, and React counts each keystroke commit made while that
    // render waits. When ~50 keystrokes ran before it (fast typing on iPhone),
    // the next keystroke threw "Maximum update depth exceeded" (GROKIO-APP-72).
    // Measures only when the text changes: re-measuring after its own height
    // change looped where the measurement isn't stable (GROKIO-APP-2BA).
    useLayoutEffect(() => {
      if (Platform.OS !== 'web') return;

      const node = webInputRef.current as HTMLTextAreaElement | null;
      if (!node?.style) return;

      // flex: none — the textarea has flex-1, and flex-grow stretches it to the
      // wrapper's current height even with an inline height of 0, which would
      // floor scrollHeight at the current height and prevent shrinking.
      const previousInlineHeight = node.style.height;
      const previousInlineFlex = node.style.flex;
      node.style.height = '0px';
      node.style.flex = 'none';
      const measuredHeight = Math.ceil(node.scrollHeight);
      node.style.height = previousInlineHeight;
      node.style.flex = previousInlineFlex;

      const nextHeight = Math.max(MIN_INPUT_HEIGHT, Math.min(MAX_INPUT_HEIGHT, measuredHeight));
      setInputHeight((currentHeight) =>
        currentHeight === nextHeight ? currentHeight : nextHeight
      );
    }, [inputText]);

    // Keep internal state in sync if initialText prop changes (e.g., restored drafts)
    useEffect(() => {
      setInputText(initialText);
    }, [initialText]);

    /**
     * Handle the submission of text
     * - Validates non-empty content
     * - Calls the onSubmit callback
     * - Clears the input field
     */
    const handleSubmit = async () => {
      const trimmed = inputText.trim();
      if (!trimmed || submitInFlightRef.current || isSubmitting) return;
      submitInFlightRef.current = true;
      const textToSubmit = trimmed;

      try {
        if (keepKeyboardOpenAfterSubmit) {
          scheduleRefocusInput();
        }

        await onSubmit(textToSubmit);
        setInputText('');

        // iOS-only RN new-arch regression (facebook/react-native#55059):
        // programmatically clearing a controlled multiline input doesn't re-run
        // layout, so the field keeps its multi-line height. Setting text
        // natively forces a re-measure back to a single line. Android re-lays
        // out correctly on clear (verified on emulator) and doesn't need this.
        if (Platform.OS === 'ios') {
          nativeInputRef.current?.setNativeProps({ text: '' });
        }

        if (keepKeyboardOpenAfterSubmit) {
          scheduleRefocusInput();
        }
      } finally {
        submitInFlightRef.current = false;
      }
    };

    const toolbar = (
      <View
        className={`flex-row items-end ${!isLargeScreen ? '-mx-5 px-5' : ''} pt-4`}>
        {imageUploadMode !== 'none' && (
          <ImageUploadButton
            mode={imageUploadMode}
            onImageReady={onImageUpload}
            isUploading={isImageUploading}
            isDisabled={isSubmitting}
            className="mr-2"
            showCaptionInPreview={showImageCaptionInPreview}
            dismissKeyboardOnPick={dismissKeyboardOnImagePick}
          />
        )}
        {renderTextInputField()}
        <ButtonWrapper
          key={Platform.OS === 'ios' ? (inputText.trim() ? 'send-on' : 'send-off') : undefined}
          icon={SentIcon}
          size="lg"
          variant="solid"
          action="primary"
          isDisabled={isSubmitting || isImageUploading || !inputText.trim()}
          onPress={
            Platform.OS === 'ios'
              ? () => {}
              : () => void handleSubmit()
          }
          onPressIn={
            Platform.OS === 'ios' ? () => void handleSubmit() : undefined
          }
          className="native:mb-1 ml-2 h-10 w-10 rounded-full"
          iconClassName="color-white"
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          preventFocusOnMouseDown
        />
      </View>
    );

    return (
      <>
        {toolbar}
        <ContextSheet
          isOpen={isGifPickerOpen}
          onClose={() => setIsGifPickerOpen(false)}
          contentComponent={GifPickerSheet}
          title={t('media.gif_button_label')}
          payload={{
            onSelect: async (item: IMediaItem, ctx?: IMediaShareContext) => {
              await onMediaSelected?.(item, ctx);
            },
          }}
        />
      </>
    );
  }
);

// Add display name for debugging and dev tools
BottomTextInput.displayName = 'BottomTextInput';
